import { useCallback } from 'react';
import { FormDefinition, mergeFlatDraft, mergeSectionDraft } from '@/modules/shared/hr-form-engine';

/** Answers typed but not submitted yet, kept in the browser (legacy: localStorage autosave of the review form). */
export interface SubmissionDraft {
  fingerprint: string;
  definition: FormDefinition;
  projectIds: string[];
  comment: string;
}

const draftKey = (detailId: string) => `hr-review-draft:${detailId}`;

/**
 * Drafts are tied to the exact form structure they were typed against (fingerprint): if the form changed since, the
 * draft is dropped instead of being applied to different questions.
 */
export const useSubmissionDraft = (detailId?: string) => {
  const load = useCallback(
    (fingerprint: string, current: FormDefinition): Partial<SubmissionDraft> | null => {
      if (!detailId) return null;
      try {
        const raw = localStorage.getItem(draftKey(detailId));
        if (!raw) return null;
        const draft = JSON.parse(raw) as SubmissionDraft;
        if (draft.fingerprint !== fingerprint) return null;
        return {
          ...draft,
          definition: current.HasSections
            ? { ...current, Sections: mergeSectionDraft(current.Sections, draft.definition.Sections ?? []) }
            : { ...current, FormData: mergeFlatDraft(current.FormData, draft.definition.FormData ?? []) },
        };
      } catch {
        return null;
      }
    },
    [detailId]
  );

  const save = useCallback(
    (draft: SubmissionDraft) => {
      if (!detailId) return;
      try {
        localStorage.setItem(draftKey(detailId), JSON.stringify(draft));
      } catch {
        // storage full or unavailable: the draft is a convenience, never a requirement
      }
    },
    [detailId]
  );

  const clear = useCallback(() => {
    if (!detailId) return;
    try {
      localStorage.removeItem(draftKey(detailId));
    } catch {
      // nothing to clear
    }
  }, [detailId]);

  return { load, save, clear };
};
